import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Step {
  label: string;
  description?: string;
}

interface Props {
  steps: Step[];
  current: number; // 0-indexed
  onJump?: (i: number) => void;
}

export function Stepper({ steps, current, onJump }: Props) {
  return (
    <ol className="flex items-center gap-2 sm:gap-4 overflow-x-auto pb-2">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={i} className="flex items-center gap-2 sm:gap-4 min-w-fit">
            <button
              type="button"
              disabled={!onJump || i > current}
              onClick={() => onJump?.(i)}
              className={cn(
                'flex items-center gap-2.5 group',
                onJump && i <= current && 'cursor-pointer',
                (!onJump || i > current) && 'cursor-default'
              )}
            >
              <span
                className={cn(
                  'inline-flex w-7 h-7 rounded-full items-center justify-center text-xs font-semibold transition-colors flex-shrink-0',
                  done
                    ? 'bg-brand-600 text-white'
                    : active
                      ? 'bg-brand-100 text-brand-700 ring-2 ring-brand-200'
                      : 'bg-slate-100 text-slate-500'
                )}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </span>
              <span className="hidden sm:flex flex-col items-start min-w-0 max-w-[160px]">
                <span
                  className={cn(
                    'text-sm font-medium',
                    active ? 'text-slate-900' : done ? 'text-slate-700' : 'text-slate-400'
                  )}
                >
                  {s.label}
                </span>
                {s.description && (
                  <span className="text-[11px] text-slate-400 truncate">
                    {s.description}
                  </span>
                )}
              </span>
            </button>
            {i < steps.length - 1 && (
              <span
                className={cn(
                  'w-8 sm:w-12 h-px',
                  done ? 'bg-brand-300' : 'bg-slate-200'
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
